'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createBrowserClient } from '@supabase/ssr';
import { Session, User, AuthError } from '@supabase/supabase-js';
import { useRouter } from 'next/navigation';
import { captureEvent, resetUser } from '@/lib/utils/posthog';
import {
  getSupabaseConfig,
  logEnvironmentConfig
} from '@/lib/config/environment';

type AuthContextType = {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ error: AuthError | null }>;
  signUp: (
    email: string,
    password: string,
    userData: Record<string, unknown>
  ) => Promise<{
    error: AuthError | null;
    data?: {
      user: User | null;
    };
  }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  // Use environment-specific Supabase configuration
  const config = getSupabaseConfig();
  const supabase = createBrowserClient(config.url, config.anonKey);

  // Log environment configuration in development/preview
  useEffect(() => {
    logEnvironmentConfig();
  }, []);

  useEffect(() => {
    const getSession = async () => {
      setIsLoading(true);

      const {
        data: { session },
        error
      } = await supabase.auth.getSession();

      if (error) {
        console.error('Error loading session:', error);
      } else {
        setSession(session);
        setUser(session?.user ?? null);
      }

      setIsLoading(false);

      // Set up listener for auth state changes
      const {
        data: { subscription }
      } = await supabase.auth.onAuthStateChange((_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
      });

      return () => {
        subscription.unsubscribe();
      };
    };

    getSession();
  }, [supabase]);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) return { error };

      const {
        data: {
          session: { user }
        }
      } = await supabase.auth.getSession();

      if (user) {
        captureEvent('login', {
          distinct_id: user.id,
          email: user.email,
          auth_type: user.app_metadata.provider || 'email'
        });
      }

      return { error };
    } catch (error) {
      console.log('Error signing in:', error);
      return { error: error as AuthError };
    }
  };

  const signUp = async (
    email: string,
    password: string,
    userData: Record<string, unknown>
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData
      }
    });
    const { user } = data;

    if (user) {
      captureEvent('signup', {
        distinct_id: user.id,
        email: user.email,
        auth_type: user.app_metadata.provider || 'email'
      });
    }

    return {
      error,
      data: {
        user: data.user
      }
    };
  };

  const signOut = async () => {
    // Reset PostHog user tracking before signing out
    resetUser();
    await supabase.auth.signOut();
    router.push('/login');
  };

  const value = {
    user,
    session,
    isLoading,
    signIn,
    signUp,
    signOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
